import React, { useState, useEffect } from "react";
import axios from "axios";

const AdminServicesManager = () => {
  const [services, setServices] = useState([]);
  const [currentService, setCurrentService] = useState({
    category: "",
    label: "",
    value: 0,
    id: null,
  });
  const [loading, setLoading] = useState(false);
  const accessToken = localStorage.getItem("authToken");

  const fetchServices = async () => {
    try {
      const response = await axios.get(
        "https://365luxuryhomes.com/api/services"
      );
      setServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleFormChange = (field, value) => {
    setCurrentService((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddOrUpdateService = async () => {
    if (
      !currentService.category ||
      !currentService.label ||
      !currentService.value
    ) {
      return alert("Please fill in all fields.");
    }

    setLoading(true);
    try {
      if (currentService.id) {
        await axios.put(
          `https://365luxuryhomes.com/api/services/${currentService.id}`,
          currentService,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        alert("Service updated successfully!");
      } else {
        const response = await axios.post(
          "https://365luxuryhomes.com/api/services",
          currentService,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        setServices((prev) => [...prev, response.data]);
        alert("Service added successfully!");
      }

      setCurrentService({ category: "", label: "", value: 0, id: null });
      fetchServices();
    } catch (error) {
      console.error("Error adding/updating service:", error);
      alert("Failed to save service.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditService = (service) => {
    setCurrentService({
      category: service.category,
      label: service.label,
      value: parseInt(service.value),
      id: service._id,
    });
  };

  const handleDeleteService = async (id) => {
    if (window.confirm("Are you sure you want to delete this service?")) {
      try {
        await axios.delete(`https://365luxuryhomes.com/api/services/${id}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        alert("Service deleted successfully!");
        fetchServices();
      } catch (error) {
        console.error("Error deleting service:", error);
      }
    }
  };

  const ServiceCard = ({ service, onEdit, onDelete }) => (
    <div className="admin-card flex flex-row justify-between items-center gap-4">
      <div>
        <h3 className="font-semibold text-base-content">{service.label}</h3>
        <p className="text-sm text-base-content/60">Value: AED {service.value}</p>
      </div>
      <div className="flex gap-2 shrink-0">
        <button
          type="button"
          onClick={() => onEdit(service)}
          className="btn btn-ghost btn-square btn-sm rounded-admin"
          aria-label="Edit"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15.2141 5.98239L16.6158 4.58063C17.39 3.80646 18.6452 3.80646 19.4194 4.58063C20.1935 5.3548 20.1935 6.60998 19.4194 7.38415L18.0176 8.78591M15.2141 5.98239L6.98023 14.2163C5.93493 15.2616 5.41226 15.7842 5.05637 16.4211C4.70047 17.058 4.3424 18.5619 4 20C5.43809 19.6576 6.94199 19.2995 7.57889 18.9436C8.21579 18.5877 8.73844 18.0651 9.78375 17.0198L18.0176 8.78591M15.2141 5.98239L18.0176 8.78591" />
            <path d="M11 20H17" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => onDelete(service._id)}
          className="btn btn-error btn-square btn-sm rounded-admin"
          aria-label="Delete"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <path d="M19.5 5.5L18.8803 15.5251C18.7219 18.0864 18.6428 19.3671 18.0008 20.2879C17.6833 20.7431 17.2747 21.1273 16.8007 21.416C15.8421 22 14.559 22 11.9927 22C9.42312 22 8.1383 22 7.17905 21.4149C6.7048 21.1257 6.296 20.7408 5.97868 20.2848C5.33688 19.3626 5.25945 18.0801 5.10461 15.5152L4.5 5.5" />
            <path d="M3 5.5H21M16.0557 5.5L15.3731 4.09173C14.9196 3.15626 14.6928 2.68852 14.3017 2.39681C14.215 2.3321 14.1231 2.27454 14.027 2.2247C13.5939 2 13.0741 2 12.0345 2C10.9688 2 10.436 2 9.99568 2.23412C9.8981 2.28601 9.80498 2.3459 9.71729 2.41317C9.32164 2.7167 9.10063 3.20155 8.65861 4.17126L8.05292 5.5" />
            <path d="M9.5 16.5L9.5 10.5" />
            <path d="M14.5 16.5L14.5 10.5" />
          </svg>
        </button>
      </div>
    </div>
  );

  return (
    <div className="admin-page">
      <h1 className="admin-page-title">Manage Add-On & Additional Services</h1>
      <p className="admin-page-desc">Add or edit service items and their values.</p>

      <div className="admin-card mb-6">
        <h2 className="admin-card-title">
          {currentService.id ? "Update Service" : "Add New Service"}
        </h2>
        <div className="space-y-4">
          <div>
            <label className="admin-label">Category</label>
            <select
              value={currentService.category}
              onChange={(e) => handleFormChange("category", e.target.value)}
              className="admin-input"
            >
              <option value="">Select Category</option>
              <option value="Add-On">Add-On</option>
              <option value="Additional">Additional</option>
            </select>
          </div>
          <div>
            <label className="admin-label">Service Label</label>
            <input
              type="text"
              placeholder="Service Label"
              value={currentService.label}
              onChange={(e) => handleFormChange("label", e.target.value)}
              className="admin-input"
            />
          </div>
          <div>
            <label className="admin-label">Service Value (AED)</label>
            <input
              type="number"
              placeholder="Service Value"
              value={currentService.value || ""}
              onChange={(e) => handleFormChange("value", parseInt(e.target.value) || 0)}
              className="admin-input"
            />
          </div>
          <button
            type="button"
            onClick={handleAddOrUpdateService}
            className="btn btn-primary rounded-admin"
            disabled={loading}
          >
            {loading ? "Saving..." : currentService.id ? "Update Service" : "Add Service"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="admin-card-title">Add-On Services</h2>
          <div className="space-y-3">
            {services
              .filter((s) => s.category === "Add-On")
              .map((service) => (
                <ServiceCard
                  key={service._id}
                  service={service}
                  onEdit={handleEditService}
                  onDelete={handleDeleteService}
                />
              ))}
          </div>
        </div>
        <div>
          <h2 className="admin-card-title">Additional Services</h2>
          <div className="space-y-3">
            {services
              .filter((s) => s.category === "Additional")
              .map((service) => (
                <ServiceCard
                  key={service._id}
                  service={service}
                  onEdit={handleEditService}
                  onDelete={handleDeleteService}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminServicesManager;
